import React, { useRef, useEffect } from 'react';

/* ════════════════════════════════════════════════════════════════
   Hero — 3D Isometric Construction Site Animation
   ─────────────────────────────────────────────────────────────
   Pure Canvas 2D. No external libraries. Zero dependencies.

   Scene:
   • ~20 isometric buildings with animated windows
   • Dominant 50-floor hero tower with spire + glow
   • Tower crane with moving trolley, swinging load, cables
   • Ground grid plane, podium platform
   • Floating dust particles
   • Mouse parallax — move cursor to tilt the scene
   • HUD: scan line, corner brackets, live readout
════════════════════════════════════════════════════════════════ */

/* ── Brand colour helpers ── */
const GOLD  = (a) => `rgba(30,40,35,${a})`;
const GRN   = (a) => `rgba(250,250,250,${a})`;
const GRN2  = (a) => `rgba(240,240,240,${a})`;
const GRN3  = (a) => `rgba(230,230,230,${a})`;
const SAGE  = (a) => `rgba(200,200,200,${a})`;
const MINT  = (a) => `rgba(220,220,220,${a})`;
const CREAM = (a) => `rgba(51,51,51,${a})`;

/* ── Pure isometric projection with mouse-parallax tilt ──
   x,z = ground plane axes  |  y = height
   tiltX, tiltY = parallax offsets driven by mouse position */
function iso(x, y, z, ox, oy, sc, tiltX = 0, tiltY = 0) {
  const tx = x + tiltX * z * 0.012;
  const tz = z + tiltY * x * 0.008;
  const px = (tx - tz) * Math.cos(Math.PI / 6) * sc;
  const py = (tx + tz) * Math.sin(Math.PI / 6) * sc - y * sc;
  return [ox + px, oy + py];
}

/* ── Draw a filled/stroked polygon ── */
function drawFace(ctx, pts, fill, stroke, sw = 1) {
  if (!pts.length) return;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  if (fill)   { ctx.fillStyle = fill;     ctx.fill();   }
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = sw; ctx.stroke(); }
}

/* ── Window state manager ──
   Each window independently and slowly changes between lit/dark.
   Uses stable keys so state persists across frames. */
const WIN_STATES = {};
function isWindowLit(key, now) {
  if (!WIN_STATES[key]) {
    WIN_STATES[key] = { val: Math.random() > 0.4 ? 1 : 0, next: Math.random() * 8000 + 4000, last: now };
  }
  const ws = WIN_STATES[key];
  if (now - ws.last > ws.next) {
    ws.val   = ws.val > 0.5 ? 0 : 1;
    ws.next  = Math.random() * 8000 + 4000;
    ws.last  = now;
  }
  return ws.val > 0.5;
}

/* ── Generic building renderer ──
   Draws left/right/front faces + floor lines + animated windows.
   tg = gold top face (premium buildings)                         */
function renderBuilding(ctx, x, z, w, d, h, fh, wf, wsC, tg, sc, ox, oy, tX, tY, now) {
  const I = (px, py, pz) => iso(px, py, pz, ox, oy, sc, tX, tY);
  const floors = Math.max(2, Math.floor(h / fh));

  // Back/shadow face
  drawFace(ctx, [I(x,0,z),I(x,0,z+d),I(x,h,z+d),I(x,h,z)], GRN2(.92), GOLD(.1));
  // Right face
  drawFace(ctx, [I(x+w,0,z),I(x+w,0,z+d),I(x+w,h,z+d),I(x+w,h,z)], GRN3(.92), GOLD(.12));

  // Front face — gradient strips for depth
  for (let s = 0; s < 12; s++) {
    const y0 = h*(s/12), y1 = h*((s+1)/12), tm = s/12;
    const ri=Math.round(240+tm*15), gi=Math.round(240+tm*15), bi=Math.round(240+tm*15);
    drawFace(ctx, [I(x,y0,z+d),I(x+w,y0,z+d),I(x+w,y1,z+d),I(x,y1,z+d)],
      `rgba(${ri},${gi},${bi},0.96)`, null);
  }
  drawFace(ctx, [I(x,0,z+d),I(x+w,0,z+d),I(x+w,h,z+d),I(x,h,z+d)], null, GOLD(.22));

  // Top face
  drawFace(ctx,
    [I(x,h,z),I(x+w,h,z),I(x+w,h,z+d),I(x,h,z+d)],
    tg ? GOLD(.22) : GOLD(.14),
    tg ? GOLD(.78) : GOLD(.42),
    tg ? 2 : 1
  );

  // Floor separation lines
  for (let f = 1; f < floors; f++) {
    const fy = f * fh, lw = f%3===0?1.5:0.8, la = f%3===0?0.18:0.09;
    const [a, b] = [I(x,fy,z+d), I(x+w,fy,z+d)];
    ctx.beginPath(); ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]);
    ctx.strokeStyle = GOLD(la); ctx.lineWidth = lw; ctx.stroke();
    const [c2, dd] = [I(x+w,fy,z), I(x+w,fy,z+d)];
    ctx.beginPath(); ctx.moveTo(c2[0],c2[1]); ctx.lineTo(dd[0],dd[1]);
    ctx.strokeStyle = GOLD(.12); ctx.lineWidth = 0.7; ctx.stroke();
  }

  // Front windows
  if (wf > 0 && floors > 2) {
    for (let f = 1; f < floors; f++) {
      const fy=f*fh, ww=w*.68/wf, wp=(w*.32)/(wf+1), wh=fh*.58;
      for (let c = 0; c < wf; c++) {
        const wx = x + wp*(c+1) + c*ww;
        const lit = isWindowLit(`${x}${z}f${f}c${c}`, now);
        const pts = [I(wx,fy+.07,z+d+.01),I(wx+ww,fy+.07,z+d+.01),
                     I(wx+ww,fy+.07+wh,z+d+.01),I(wx,fy+.07+wh,z+d+.01)];
        drawFace(ctx, pts, lit ? GOLD(.62) : MINT(.18), null);
        if (lit && (f+c)%5===0) {
          drawFace(ctx, [I(wx,fy+.07,z+d+.02),I(wx+ww*.3,fy+.07,z+d+.02),
                         I(wx+ww*.15,fy+wh*.9,z+d+.02),I(wx,fy+wh*.6,z+d+.02)],
                   CREAM(.14), null);
        }
      }
    }
  }

  // Right face windows
  if (wsC > 0 && floors > 2) {
    for (let f = 1; f < floors; f++) {
      const fy=f*fh, ww=d*.55/wsC, wp=d*.45/(wsC+1), wh=fh*.5;
      for (let c = 0; c < wsC; c++) {
        const wz = z + wp*(c+1) + c*ww;
        const lit = isWindowLit(`${x}${z}r${f}c${c}`, now);
        drawFace(ctx,
          [I(x+w+.01,fy+.07,wz),I(x+w+.01,fy+.07,wz+ww),
           I(x+w+.01,fy+.07+wh,wz+ww),I(x+w+.01,fy+.07+wh,wz)],
          lit ? GOLD(.42) : SAGE(.15), null);
      }
    }
  }
}

/* ── Tower crane ──
   Mast, jib, counterweight, moving trolley, swinging load.       */
function renderCrane(ctx, sc, ox, oy, tX, tY, now) {
  const I = (x, y, z) => iso(x, y, z, ox, oy, sc, tX, tY);

  // Mast
  drawFace(ctx, [I(2.2,0,0),I(2.6,0,0),I(2.6,16,0),I(2.2,16,0)], GRN3(.85), GOLD(.18));
  drawFace(ctx, [I(2.2,0,0),I(2.2,0,.4),I(2.2,16,.4),I(2.2,16,0)], GRN2(.82), GOLD(.14));
  drawFace(ctx, [I(2.2,16,0),I(2.6,16,0),I(2.6,16,.4),I(2.2,16,.4)], GOLD(.18), GOLD(.6), 1.5);

  // Jib
  const JY = 16;
  drawFace(ctx, [I(-4,JY,0),I(6,JY,0),I(6,JY,.4),I(-4,JY,.4)], GRN3(.9), GOLD(.25));
  drawFace(ctx, [I(-4,JY,0),I(6,JY,0),I(6,JY+.5,0),I(-4,JY+.5,0)], GRN3(.8), GOLD(.2));
  drawFace(ctx, [I(-4,JY,.4),I(6,JY,.4),I(6,JY+.5,.4),I(-4,JY+.5,.4)], GOLD(.1), GOLD(.4));

  // Counterweight
  drawFace(ctx, [I(-6,JY,0),I(-3.8,JY,0),I(-3.8,JY+.5,0),I(-6,JY+.5,0)], GRN3(.85), GOLD(.2));
  drawFace(ctx, [I(-6,JY,.4),I(-3.8,JY,.4),I(-3.8,JY+.5,.4),I(-6,JY+.5,.4)], GOLD(.1), GOLD(.38));
  drawFace(ctx,
    [I(-6.5,JY+.5,-.3),I(-5,JY+.5,-.3),I(-5,JY+1.8,-.3),I(-6.5,JY+1.8,-.3)],
    GRN3(.9), GOLD(.3));
  drawFace(ctx,
    [I(-6.5,JY+.5,-.3),I(-6.5,JY+.5,.7),I(-6.5,JY+1.8,.7),I(-6.5,JY+1.8,-.3)],
    GRN2(.85), GOLD(.25));

  // Moving trolley
  const trolleyX = -3 + Math.sin(now * 0.0007) * 4.2;
  drawFace(ctx,
    [I(trolleyX,JY+.5,0),I(trolleyX+.5,JY+.5,0),I(trolleyX+.5,JY+.5,.4),I(trolleyX,JY+.5,.4)],
    GOLD(.75), GOLD(.9));

  // Hook cable
  const dropY  = JY + .5 - 2.2 - Math.sin(now * 0.0005) * 1.4;
  const hcA = I(trolleyX+.25, JY+.5, .2);
  const hcB = I(trolleyX+.25, dropY, .2);
  ctx.beginPath(); ctx.moveTo(hcA[0],hcA[1]); ctx.lineTo(hcB[0],hcB[1]);
  ctx.strokeStyle = GOLD(.55); ctx.lineWidth = 1.2; ctx.stroke();

  // Swinging load box
  const swing = Math.sin(now * 0.00035) * 0.07;
  const lx    = trolleyX + .25 + Math.sin(swing) * 1.2;
  const lY    = dropY;

  // Hook block
  drawFace(ctx, [I(lx-.25,lY,.05),I(lx+.25,lY,.05),I(lx+.25,lY+.45,.05),I(lx-.25,lY+.45,.05)], GRN3(.9), GOLD(.55));
  drawFace(ctx, [I(lx-.25,lY,.05),I(lx-.25,lY,.45),I(lx-.25,lY+.45,.45),I(lx-.25,lY+.45,.05)], GRN2(.85), GOLD(.45));
  drawFace(ctx, [I(lx-.25,lY,.45),I(lx+.25,lY,.45),I(lx+.25,lY+.45,.45),I(lx-.25,lY+.45,.45)], GOLD(.2), GOLD(.7));

  // Load box faces
  const lb = [
    [lx-.7,lY+.5,-.1],[lx+.7,lY+.5,-.1],[lx+.7,lY+.5,.6],[lx-.7,lY+.5,.6],
    [lx-.7,lY+.5+1.4,-.1],[lx+.7,lY+.5+1.4,-.1],[lx+.7,lY+.5+1.4,.6],[lx-.7,lY+.5+1.4,.6],
  ];
  drawFace(ctx, [I(...lb[0]),I(...lb[1]),I(...lb[5]),I(...lb[4])], GRN3(.88), GOLD(.22));
  drawFace(ctx, [I(...lb[2]),I(...lb[3]),I(...lb[7]),I(...lb[6])], GRN3(.92), GOLD(.28));
  drawFace(ctx, [I(...lb[0]),I(...lb[3]),I(...lb[7]),I(...lb[4])], GRN2(.85), GOLD(.2));
  drawFace(ctx, [I(...lb[4]),I(...lb[5]),I(...lb[6]),I(...lb[7])], GOLD(.15), GOLD(.5));

  // Ropes to load
  const rA = I(lx, lY+.48, .25);
  [[lb[4]],[lb[5]]].forEach(([pt]) => {
    const rb = I(pt[0], pt[1], pt[2]);
    ctx.beginPath(); ctx.moveTo(rA[0],rA[1]); ctx.lineTo(rb[0],rb[1]);
    ctx.strokeStyle = GOLD(.38); ctx.lineWidth = 0.8; ctx.stroke();
  });

  // Support cables
  const mastTop = I(2.4, JY, .2);
  [I(-4,JY,.2), I(6,JY,.2)].forEach(tip => {
    ctx.beginPath(); ctx.moveTo(mastTop[0],mastTop[1]); ctx.lineTo(tip[0],tip[1]);
    ctx.strokeStyle = GOLD(.28); ctx.lineWidth = 0.9;
    ctx.setLineDash([4, 4]); ctx.stroke();
  });
  ctx.setLineDash([]);
}

/* ── Dust particles (initialised once) ── */
function makeParticles(n = 55) {
  return Array.from({ length: n }, () => ({
    x:   (Math.random() - 0.5) * 18,
    y:    Math.random() * 18 - 1,
    z:   (Math.random() - 0.5) * 18,
    vx:  (Math.random() - 0.5) * 0.006,
    vy:   Math.random() * 0.004 + 0.001,
    vz:  (Math.random() - 0.5) * 0.006,
    r:    Math.random() * 1.4 + 0.4,
    a:    Math.random() * 0.32 + 0.06,
    gold: Math.random() > 0.55,
  }));
}

/* ════════════════════════════════════════════════════════════════
   HERO COMPONENT
════════════════════════════════════════════════════════════════ */
export default function Hero() {
  const canvasRef  = useRef(null);
  const mouseRef   = useRef({ x: 0.5, y: 0.5 });
  const dustRef    = useRef(makeParticles());
  const rafRef     = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let W, H;

    // Resize canvas to match its CSS size
    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Track mouse for parallax
    const onMouse = (e) => {
      const r = canvas.getBoundingClientRect();
      mouseRef.current = { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
    };
    canvas.addEventListener('mousemove', onMouse);

    const render = (now) => {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#FAFAFA';
      ctx.fillRect(0, 0, W, H);

      // Camera / scale
      const SC = Math.min(W, H) * 0.042;
      const OX = W * 0.78;
      const OY = H * 0.62;

      // Mouse parallax tilt
      const tX = (mouseRef.current.x - 0.5) * 1.8;
      const tY = (mouseRef.current.y - 0.5) * 1.2;

      const I = (x, y, z) => iso(x, y, z, OX, OY, SC, tX, tY);

      // ── Ground plane
      const GS = 20;
      drawFace(ctx, [I(-GS,0,-GS),I(GS,0,-GS),I(GS,0,GS),I(-GS,0,GS)], GRN(.96), GOLD(.12));
      for (let i = -GS; i <= GS; i += 2) {
        ctx.beginPath(); const [a,b]=[I(i,0,-GS),I(i,0,GS)]; ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]);
        ctx.strokeStyle = GOLD(.06); ctx.lineWidth = 0.8; ctx.stroke();
        ctx.beginPath(); const [c2,d]=[I(-GS,0,i),I(GS,0,i)]; ctx.moveTo(c2[0],c2[1]); ctx.lineTo(d[0],d[1]);
        ctx.strokeStyle = GOLD(.05); ctx.lineWidth = 0.8; ctx.stroke();
      }
      for (const i of [-2, 0, 2]) {
        ctx.beginPath(); const [a,b]=[I(i,0,-GS),I(i,0,GS)]; ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]);
        ctx.strokeStyle = GOLD(.14); ctx.lineWidth = 1.5; ctx.stroke();
      }

      // ── Helper shorthand
      const B = (x, z, w, d, h, fh, wf, ws, tg) =>
        renderBuilding(ctx, x, z, w, d, h, fh, wf, ws, tg, SC, OX, OY, tX, tY, now);

      // ── Back row
      B(-18,-18, 5,4,15, 1.4, 3,2, false);
      B( -9,-20, 6,5,21, 1.5, 4,2, false);
      B(  4,-22, 4,4,27, 1.4, 3,2, false);
      B( 12,-18, 5,4,19, 1.5, 4,2, true);

      // ── Mid row
      B(-14,-8, 7,5,17, 1.5, 5,3, true);
      B(  7,-9, 5,5,23, 1.4, 4,3, false);
      B( 14,-7, 6,5,14, 1.5, 5,3, true);

      // ── Main hero tower (50 floors)
      {
        const bx=-4, bz=-4, bw=10, bd=8.5, bh=50, fh=1.25;

        drawFace(ctx, [I(bx,0,bz),I(bx,0,bz+bd),I(bx,bh,bz+bd),I(bx,bh,bz)], GRN2(.92), GOLD(.1));
        drawFace(ctx, [I(bx+bw,0,bz),I(bx+bw,0,bz+bd),I(bx+bw,bh,bz+bd),I(bx+bw,bh,bz)], GRN3(.92), GOLD(.15));

        // Gradient front strips
        for (let s = 0; s < 24; s++) {
          const y0=bh*(s/24), y1=bh*((s+1)/24), tm=s/24;
          const ri=Math.round(230+tm*25), gi=Math.round(230+tm*25), bi=Math.round(230+tm*25);
          drawFace(ctx,
            [I(bx,y0,bz+bd),I(bx+bw,y0,bz+bd),I(bx+bw,y1,bz+bd),I(bx,y1,bz+bd)],
            `rgba(${ri},${gi},${bi},0.97)`, null);
        }
        drawFace(ctx, [I(bx,0,bz+bd),I(bx+bw,0,bz+bd),I(bx+bw,bh,bz+bd),I(bx,bh,bz+bd)], null, GOLD(.28), 2);

        // Gold top
        drawFace(ctx, [I(bx,bh,bz),I(bx+bw,bh,bz),I(bx+bw,bh,bz+bd),I(bx,bh,bz+bd)], GOLD(.24), GOLD(.82), 2.5);

        // Floors
        const floors = Math.floor(bh / fh);
        for (let f = 1; f < floors; f++) {
          const fy=f*fh, lw=f%3===0?2:1, la=f%3===0?.22:.11;
          ctx.beginPath(); const [a,b]=[I(bx,fy,bz+bd),I(bx+bw,fy,bz+bd)]; ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]);
          ctx.strokeStyle=GOLD(la); ctx.lineWidth=lw; ctx.stroke();
          ctx.beginPath(); const [c2,d]=[I(bx+bw,fy,bz),I(bx+bw,fy,bz+bd)]; ctx.moveTo(c2[0],c2[1]); ctx.lineTo(d[0],d[1]);
          ctx.strokeStyle=GOLD(.14); ctx.lineWidth=1; ctx.stroke();
        }

        // Front windows — 9 columns
        const wc = 9;
        for (let f = 1; f < floors; f++) {
          const fy=f*fh, ww=bw*.68/wc, wp=(bw*.32)/(wc+1), wh=fh*.58;
          for (let c = 0; c < wc; c++) {
            const wx = bx + wp*(c+1) + c*ww;
            const lit = isWindowLit(`mt${f}${c}`, now);
            const pts = [I(wx,fy+.07,bz+bd+.01),I(wx+ww,fy+.07,bz+bd+.01),
                         I(wx+ww,fy+.07+wh,bz+bd+.01),I(wx,fy+.07+wh,bz+bd+.01)];
            drawFace(ctx, pts, lit ? GOLD(.66) : MINT(.2), null);
            if (lit && (f+c)%4===0) {
              drawFace(ctx,
                [I(wx,fy+.07,bz+bd+.02),I(wx+ww*.35,fy+.07,bz+bd+.02),
                 I(wx+ww*.18,fy+.07+wh,bz+bd+.02),I(wx,fy+.07+wh*.68,bz+bd+.02)],
                CREAM(.14), null);
            }
          }
        }

        // Glazing bands
        for (let f = 3; f < floors; f += 4) {
          const fy = f * fh;
          drawFace(ctx,
            [I(bx,fy,bz+bd+.02),I(bx+bw,fy,bz+bd+.02),I(bx+bw,fy+.18,bz+bd+.02),I(bx,fy+.18,bz+bd+.02)],
            SAGE(.18), GOLD(.1));
        }

        // Right windows — 6 cols
        const wc2 = 6;
        for (let f = 1; f < floors; f++) {
          const fy=f*fh, ww=bd*.55/wc2, wp=bd*.45/(wc2+1), wh=fh*.5;
          for (let c = 0; c < wc2; c++) {
            const wz = bz + wp*(c+1) + c*ww;
            const lit = isWindowLit(`mtr${f}${c}`, now);
            drawFace(ctx,
              [I(bx+bw+.01,fy+.07,wz),I(bx+bw+.01,fy+.07,wz+ww),
               I(bx+bw+.01,fy+.07+wh,wz+ww),I(bx+bw+.01,fy+.07+wh,wz)],
              lit ? GOLD(.45) : SAGE(.15), null);
          }
        }

        // Lobby — double-height ground floor windows
        const wh_l = fh * 1.8;
        for (let c = 0; c < wc; c++) {
          const wx = bx + (bw*.32/(wc+1))*(c+1) + c*(bw*.68/wc);
          const ww_l = bw * .68 / wc;
          drawFace(ctx,
            [I(wx,.18,bz+bd+.01),I(wx+ww_l,.18,bz+bd+.01),
             I(wx+ww_l,.18+wh_l,bz+bd+.01),I(wx,.18+wh_l,bz+bd+.01)],
            GOLD(.48), GOLD(.35));
        }

        // Spire
        const ST = I(bx+bw/2, bh+8.5, bz+bd/2);
        const SB = [
          I(bx+bw/2-1.5,bh,bz+bd/2-1.5), I(bx+bw/2+1.5,bh,bz+bd/2-1.5),
          I(bx+bw/2+1.5,bh,bz+bd/2+1.5), I(bx+bw/2-1.5,bh,bz+bd/2+1.5),
        ];
        for (let i = 0; i < 4; i++) {
          drawFace(ctx, [SB[i], SB[(i+1)%4], ST],
            i%2===0 ? GOLD(.32) : GOLD(.2), GOLD(.62));
        }

        // Spire glow (pulsing radial)
        const spPt   = I(bx+bw/2, bh+8.5, bz+bd/2);
        const pulse  = 0.85 + 0.15 * Math.sin(now * 0.002);
        const spGrad = ctx.createRadialGradient(spPt[0],spPt[1],0, spPt[0],spPt[1],80*pulse);
        spGrad.addColorStop(0, GOLD(0.45 * pulse));
        spGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = spGrad;
        ctx.beginPath(); ctx.arc(spPt[0], spPt[1], 80*pulse, 0, Math.PI*2); ctx.fill();
      }

      // ── Foreground buildings
      B(-17, 4, 7,5,13, 1.4, 5,3, true);
      B( -9, 5, 8,6,17, 1.5, 6,4, false);
      B(  9, 2, 9,7, 8, 1.3, 7,4, true);
      B(-19, 9, 5,4, 9, 1.3, 4,2, false);
      B( 13, 5, 6,5,11, 1.3, 5,3, false);

      // ── Podium platform
      drawFace(ctx, [I(-5,3.8,-5.5),I(6,3.8,-5.5),I(6,3.8,4.5),I(-5,3.8,4.5)], 'rgba(250,250,250,.85)', GOLD(.35));
      drawFace(ctx, [I(-5,0,4.5),I(6,0,4.5),I(6,3.8,4.5),I(-5,3.8,4.5)],        GRN3(.9),            GOLD(.25));
      drawFace(ctx, [I(6,0,-5.5),I(6,0,4.5),I(6,3.8,4.5),I(6,3.8,-5.5)],        GRN(.88),            GOLD(.2));

      // ── Tower crane
      renderCrane(ctx, SC, OX, OY, tX, tY, now);

      // ── Dust particles
      dustRef.current.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.z += p.vz;
        if (p.y > 20) { p.y = -1; p.x = (Math.random()-.5)*18; p.z = (Math.random()-.5)*18; }
        const pp = I(p.x, p.y, p.z);
        ctx.beginPath(); ctx.arc(pp[0], pp[1], p.r, 0, Math.PI*2);
        ctx.fillStyle = p.gold ? GOLD(p.a * .6) : `rgba(204,27,34,${p.a*.45})`;
        ctx.fill();
      });

      // ── Base ambient glow
      const baseG  = I(0, 0, 0);
      const baseGr = ctx.createRadialGradient(baseG[0],baseG[1],0, baseG[0],baseG[1],SC*12);
      baseGr.addColorStop(0, GOLD(.07)); baseGr.addColorStop(1, 'transparent');
      ctx.save(); ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = baseGr;
      ctx.beginPath(); ctx.ellipse(baseG[0], baseG[1]+SC*2, SC*10, SC*4, 0, 0, Math.PI*2); ctx.fill();
      ctx.restore();

      // ── HUD: corner brackets
      const m=14, l=18;
      [[m,m,1,1],[W-m,m,-1,1],[m,H-m,1,-1],[W-m,H-m,-1,-1]].forEach(([bx,by,hd,vd]) => {
        ctx.beginPath();
        ctx.moveTo(bx,by); ctx.lineTo(bx+hd*l,by);
        ctx.moveTo(bx,by); ctx.lineTo(bx,by+vd*l);
        ctx.strokeStyle = GOLD(.32); ctx.lineWidth = 1.5; ctx.stroke();
      });

      // ── HUD: scan line
      const scanY = ((now * 0.045) % H);
      ctx.beginPath(); ctx.moveTo(0,scanY); ctx.lineTo(W,scanY);
      ctx.strokeStyle = GOLD(.04); ctx.lineWidth = 1; ctx.stroke();

      // ── HUD: text readouts
      ctx.fillStyle = GOLD(.3); ctx.font = 'bold 11px monospace'; ctx.textAlign = 'left';
      const deg = (((tX * 30)|0) + 360) % 360;
      ctx.fillText(`ISO · PARALLAX ${deg.toString().padStart(3,'0')}°`, m+2, H-m-2);
      ctx.textAlign = 'right';
      ctx.fillStyle = (Math.floor(now/900)%2===0) ? GOLD(.8) : 'rgba(204,27,34,.8)';
      ctx.fillText('● LIVE', W-m-2, H-m-2);

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMouse);
    };
  }, []);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="hero"
      style={{ position:'relative', zIndex:1, minHeight:'100vh', display:'flex', alignItems:'center', overflow:'hidden' }}
    >
      {/* Left-heavy gradient so text stays legible over the 3D scene */}
      <div style={{
        position:'absolute', inset:0,
        background:'linear-gradient(105deg,rgba(250,250,250,0.92) 38%,rgba(250,250,250,0.18) 70%,transparent 100%)',
        zIndex:0, pointerEvents:'none',
      }} />

      {/* 3D canvas — fills the entire section */}
      <canvas
        ref={canvasRef}
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', zIndex:0, cursor:'crosshair' }}
      />

      {/* Hero copy — left column */}
      <div style={{
        position:'relative', zIndex:1, width:'100%', maxWidth:1200,
        margin:'0 auto', padding:'0 2rem', paddingTop:120,
        display:'grid', gridTemplateColumns:'1fr 1fr',
        alignItems:'center', minHeight:'100vh',
      }}>
        <div>
          <p className="anim-eyebrow" style={{
            fontFamily:'var(--fh)', fontSize:'0.85rem', fontWeight:600,
            letterSpacing:'0.25em', textTransform:'uppercase',
            color:'var(--gold)', marginBottom:'1.5rem',
          }}>
            Muster Construct &amp; Consultancy Services
          </p>

          <h1 className="anim-title" style={{
            fontFamily:'var(--fh)', fontSize:'clamp(3rem,6vw,6rem)',
            fontWeight:800, lineHeight:0.95, letterSpacing:'-0.01em',
            textTransform:'uppercase', marginBottom:'2rem',
          }}>
            Turn Your<br />
            <span style={{ color:'var(--gold)', display:'block' }}>Dream House</span>
            Into Reality
          </h1>

          <p className="anim-sub" style={{
            fontSize:'1.05rem', fontWeight:500, lineHeight:1.7,
            color:'var(--lt)', maxWidth:480, marginBottom:'2.5rem',
          }}>
            Quality construction starting from 2.5Lakh Affordable Price — residential homes, commercial buildings,
            tensile structures, and PEB solutions across Salem &amp; Coimbatore.
          </p>

          <div className="anim-btns" style={{ display:'flex', gap:'1rem', flexWrap:'wrap' }}>
            <button
              onClick={() => scrollTo('#projects')}
              style={{
                background:'var(--gold)', color:'var(--dk)', fontFamily:'var(--fh)',
                fontSize:'1rem', fontWeight:700, letterSpacing:'0.08em',
                textTransform:'uppercase', padding:'0.85rem 2rem',
                border:'none', cursor:'pointer', transition:'all .2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background='var(--gold-l)'; e.currentTarget.style.transform='translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background='var(--gold)'; e.currentTarget.style.transform='none'; }}
            >
              View Our Work →
            </button>
            <button
              onClick={() => scrollTo('#contact')}
              style={{
                background:'transparent', color:'var(--wh)', fontFamily:'var(--fh)',
                fontSize:'1rem', fontWeight:600, letterSpacing:'0.08em',
                textTransform:'uppercase', padding:'0.85rem 2rem',
                border:'1.5px solid #1E2823', cursor:'pointer', transition:'all 0.3s ease',
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background='var(--wh)'; 
                e.currentTarget.style.borderColor='var(--wh)'; 
                e.currentTarget.style.color='var(--dk)'; 
                e.currentTarget.style.transform='translateY(-3px)';
                e.currentTarget.style.boxShadow='0 10px 20px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background='transparent'; 
                e.currentTarget.style.borderColor='#1E2823'; 
                e.currentTarget.style.color='var(--wh)'; 
                e.currentTarget.style.transform='none';
                e.currentTarget.style.boxShadow='none';
              }}
            >
              Get a Quote
            </button>
          </div>

          <div className="anim-stats" style={{ display:'flex', gap:'2.5rem', marginTop:'2.5rem', flexWrap:'wrap' }}>
            {[['15+','Projects Done'],['2.5Lakh','Affordable Price'],['Salem & CBE','Locations']].map(([n,l]) => (
              <div key={l}>
                <div style={{ fontFamily:'var(--fh)', fontSize:'2.2rem', fontWeight:800, color:'var(--gold)', lineHeight:1 }}>{n}</div>
                <div style={{ fontSize:'0.72rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Right column is empty — the 3D canvas fills it naturally */}
      </div>

      {/* Scroll indicator */}
      <div className="anim-scroll-ind" style={{
        position:'absolute', bottom:'2rem', left:'50%', transform:'translateX(-50%)',
        zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem',
      }}>
        
      </div>

      <style>{`
        @media (max-width: 1024px) {
          section#hero > div { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}